import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import TopBar from "../components/TopBar";
import CefrBadge from "../components/CefrBadge";
import ScoreCircle from "../components/ScoreCircle";
import B1Verdict from "../components/B1Verdict";
import SpeakingRecordings from "../components/SpeakingRecordings";
import { CEFR_TIPS, CEFR_ORDER, CEFR_COLORS, CEFR_MUTED, CEFR_LABELS, formatDateTime, formatDuration } from "../lib/cefr";
import { ArrowRight, CheckCircle, XCircle, Confetti, Lightbulb, ShareNetwork, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const SECTION_LABELS = {
  grammar: "Grammar",
  vocabulary: "Vocabulary",
  reading: "Reading",
  listening: "Listening",
  writing: "Writing",
  speaking: "Speaking",
};

export default function Results() {
  const { attemptId } = useParams();
  const [attempt, setAttempt] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/test/attempts/${attemptId}`)
      .then((r) => setAttempt(r.data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); });
  }, [attemptId]);

  if (notFound) {
    return (
      <div className="min-h-screen">
        <TopBar />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center neu rounded-3xl p-12 mt-20">
          <h1 className="text-3xl text-foreground font-bold">Attempt not found</h1>
          <Link to="/dashboard" className="text-indigo hover:underline mt-4 inline-block">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  if (!attempt) {
    return (
      <div className="min-h-screen">
        <TopBar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="font-mono-fig text-sm text-muted-foreground">Loading result…</div>
        </div>
      </div>
    );
  }

  const tips = CEFR_TIPS[attempt.cefr_level] || [];
  const cefrIdx = CEFR_ORDER.indexOf(attempt.cefr_level);

  const shareUrl = attempt?.share_slug
    ? `${window.location.origin}/share/${attempt.share_slug}`
    : null;

  const share = async () => {
    const text = `I just scored ${Math.round(attempt.total_score)}/${attempt.total_max} (${attempt.cefr_level} — ${CEFR_LABELS[attempt.cefr_level]}) on the Voxlingua English self-assessment!`;
    try {
      if (navigator.share && shareUrl) {
        await navigator.share({ title: "My English level", text, url: shareUrl });
      } else if (shareUrl) {
        await navigator.clipboard.writeText(`${text} ${shareUrl}`);
        toast.success("Result + link copied to clipboard!");
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Result copied to clipboard!");
      }
    } catch { /* cancelled */ }
  };

  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Public link copied!");
    } catch { toast.error("Could not copy link"); }
  };

  return (
    <div className="min-h-screen">
      <TopBar />

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Header cell */}
        <div className="neu rounded-3xl p-10 md:p-14">
          <div className="overline flex items-center gap-2">
            <Confetti size={12} weight="duotone" className="text-indigo" /> Your result
          </div>
          <div className="mt-6 grid md:grid-cols-2 gap-10 items-center">
            <div className="flex justify-center md:justify-start">
              <ScoreCircle score={attempt.total_score} max={attempt.total_max} cefr={attempt.cefr_level} testId="results-score-circle" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl text-foreground font-bold tracking-tight leading-tight">
                You scored{" "}
                <span className="font-mono-fig text-indigo">{Math.round(attempt.total_score)}</span>
                <span className="text-muted-foreground text-2xl"> / {attempt.total_max}</span>
              </h1>
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <CefrBadge level={attempt.cefr_level} size="lg" testId="results-cefr-badge" />
                <div className="text-lg text-foreground/80">{CEFR_LABELS[attempt.cefr_level]}</div>
                <B1Verdict score={attempt.total_score} testId="results-b1-verdict" />
              </div>
              <p className="mt-5 text-foreground/80 leading-relaxed">{attempt.cefr_description}</p>
              <div className="text-xs text-muted-foreground mt-4">
                Completed {formatDateTime(attempt.created_at)}
                {attempt.duration_seconds != null && <> · {formatDuration(attempt.duration_seconds)}</>}
              </div>

              <div className="flex flex-wrap gap-3 mt-8">
                <Link
                  to="/test"
                  data-testid="results-retake-btn"
                  className="neu-btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm"
                >
                  Retake test <ArrowRight size={14} weight="bold" />
                </Link>
                <button
                  onClick={share}
                  data-testid="results-share-btn"
                  className="neu-btn inline-flex items-center gap-2 px-6 py-3 text-sm"
                >
                  <ShareNetwork size={14} weight="bold" /> Share result
                </button>
                {shareUrl && (
                  <button
                    onClick={copyShareLink}
                    data-testid="results-copy-link-btn"
                    title={shareUrl}
                    className="neu-btn inline-flex items-center gap-2 px-6 py-3 text-sm"
                  >
                    <LinkSimple size={14} weight="bold" /> Copy public link
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* CEFR ladder */}
        <div className="neu rounded-3xl p-8 mt-6">
          <div className="overline">Where you sit on the CEFR ladder</div>
          <div className="mt-6 flex items-stretch gap-1 sm:gap-2" data-testid="results-cefr-ladder">
            {CEFR_ORDER.map((lvl, i) => {
              const active = i <= cefrIdx;
              const isMe = i === cefrIdx;
              return (
                <div key={lvl} className={`flex-1 min-w-0 flex flex-col items-center gap-2 p-1.5 sm:p-3 transition-colors ${isMe ? "bg-indigo/10" : ""}`}>
                  <div
                    className="w-full h-2 rounded-full"
                    style={{ background: isMe ? CEFR_COLORS[lvl] : active ? "hsl(var(--foreground))" : CEFR_MUTED }}
                  />
                  <div className="font-mono-fig text-sm text-foreground">{lvl}</div>
                  <div className={`hidden sm:block text-[11px] text-center break-words ${isMe ? "text-foreground font-bold" : "text-muted-foreground"}`}>
                    {CEFR_LABELS[lvl]}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section breakdown */}
        <div className="mt-6">
          <div className="px-2 mb-4">
            <div className="overline">Section breakdown</div>
            <h2 className="text-2xl mt-2 text-foreground font-bold tracking-tight">How you performed by skill</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="results-section-breakdown">
            {Object.keys(SECTION_LABELS).map((s) => {
              const score = attempt.section_scores[s] || 0;
              const max = attempt.section_max[s];
              const pct = Math.round((score / max) * 100);
              const correct = attempt.section_correct?.[s];
              const count = attempt.section_counts?.[s];
              return (
                <div key={s} className="neu rounded-3xl p-6" data-testid={`results-section-${s}`}>
                  <div className="flex items-baseline justify-between gap-4">
                    <div>
                      <div className="text-xl text-foreground font-bold">{SECTION_LABELS[s]}</div>
                      {s !== "writing" && count != null && (
                        <div className="text-xs text-muted-foreground mt-1 font-mono-fig">
                          {correct} correct / {count}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-mono-fig text-3xl text-foreground leading-none">{score}</div>
                      <div className="text-xs text-muted-foreground mt-1">of {max} · {pct}%</div>
                    </div>
                  </div>
                  <div className="mt-5 h-2 rounded-full bg-foreground/10 overflow-hidden">
                    <div className="h-full rounded-full bg-indigo transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Writing feedback */}
        {attempt.writing_feedback?.tasks ? (
          <div className="neu rounded-3xl p-8 mt-6" data-testid="results-writing-feedback">
            <div className="overline">Writing feedback</div>
            <h3 className="text-2xl mt-2 text-foreground font-bold tracking-tight">On your writing & speaking</h3>
            <div className="mt-6 grid lg:grid-cols-2 gap-4">
              {[...attempt.writing_feedback.tasks, ...[attempt.speaking_feedback, attempt.speaking_question_feedback].filter(Boolean)].map((t) => (
                <div key={t.id} className="neu-inset rounded-2xl p-6" data-testid={`results-writing-task-${t.id}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="font-bold text-foreground">{t.title}</div>
                    <div className="font-mono-fig text-foreground">{t.score}<span className="text-muted-foreground text-xs"> / {t.max}</span></div>
                  </div>
                  <ul className="mt-4 space-y-2">
                    {t.checks.map((c) => (
                      <li key={c.label} className="flex items-center gap-2 text-sm text-foreground/85">
                        {c.met ? <CheckCircle size={16} weight="duotone" className="text-emerald-600 shrink-0" /> : <XCircle size={16} weight="duotone" className="text-red-500 shrink-0" />}
                        {c.label}
                      </li>
                    ))}
                  </ul>
                  {t.id.startsWith("speaking") && attempt.speaking_override ? (
                    <p className="mt-4 text-sm font-bold text-signal" data-testid="results-speaking-adjusted">Score reviewed and adjusted by the admissions team.</p>
                  ) : (
                    <p className="mt-4 text-sm text-foreground/75 leading-relaxed">{t.notes}</p>
                  )}
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <Metric label="Words" value={t.word_count} />
                    <Metric label="Variety" value={`${Math.round(t.unique_ratio * 100)}%`} />
                    <Metric label="Linkers" value={t.linkers} />
                  </div>
                </div>
              ))}
            </div>
            {attempt.speaking_recordings?.length > 0 && (
              <div className="mt-4">
                <SpeakingRecordings attemptId={attempt.attempt_id} recordings={attempt.speaking_recordings} title="Listen to your speaking" />
              </div>
            )}
          </div>
        ) : attempt.writing_feedback && (
          <div className="neu rounded-3xl p-8 mt-6" data-testid="results-writing-feedback">
            <div className="overline">Writing feedback</div>
            <h3 className="text-2xl mt-2 text-foreground font-bold tracking-tight">On your written response</h3>
            <p className="mt-4 text-foreground/85 leading-relaxed">{attempt.writing_feedback.notes}</p>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Words" value={attempt.writing_feedback.word_count} />
              <Metric label="Vocab variety" value={`${Math.round(attempt.writing_feedback.unique_ratio * 100)}%`} />
              <Metric label="Avg. sentence" value={`${attempt.writing_feedback.avg_sentence_len} w`} />
              <Metric label="Punct. variety" value={attempt.writing_feedback.punctuation_variety} />
            </div>
          </div>
        )}

        {/* Tips */}
        <div className="neu rounded-3xl p-8 mt-6">
          <div className="overline flex items-center gap-2">
            <Lightbulb size={12} weight="duotone" className="text-indigo" /> Study tips for {attempt.cefr_level}
          </div>
          <h3 className="text-2xl mt-2 text-foreground font-bold tracking-tight">Where to focus next</h3>
          <ul className="mt-6 space-y-3" data-testid="results-tips-list">
            {tips.map((t, i) => (
              <li key={i} className="flex gap-4 items-start">
                <span className="neu-sm w-8 h-8 rounded-full flex items-center justify-center font-mono-fig text-xs text-indigo shrink-0">
                  0{i + 1}
                </span>
                <span className="text-foreground/85 leading-relaxed pt-1">{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Question review */}
        {attempt.per_question && attempt.per_question.length > 0 && (
          <div className="neu rounded-3xl p-8 mt-6">
            <div className="overline">Question review</div>
            <h2 className="text-2xl mt-2 text-foreground font-bold tracking-tight">Every question, revealed</h2>
            <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="results-question-grid">
              {attempt.per_question.map((q) => (
                <div
                  key={q.id}
                  className="rounded-xl p-3 flex items-center gap-2 bg-foreground/[0.04]"
                >
                  {q.is_correct ? (
                    <CheckCircle size={18} weight="duotone" className="text-emerald-600" />
                  ) : (
                    <XCircle size={18} weight="duotone" className="text-red-500" />
                  )}
                  <div className="font-mono-fig text-xs text-foreground/80">{q.number ? `Q${q.number}` : q.id}{q.type === "text" && !q.is_correct ? ` · ${q.correct}` : ""}</div>
                  <div className="ml-auto text-[10px] text-muted-foreground uppercase tracking-wider">{q.section.slice(0,3)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 text-center">
          <Link
            to="/dashboard"
            data-testid="results-back-dashboard-link"
            className="neu-btn inline-flex items-center gap-2 px-6 py-3 text-sm"
          >
            Back to dashboard <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-2xl p-4 bg-foreground/[0.04]">
      <div className="overline">{label}</div>
      <div className="font-mono-fig text-2xl text-foreground mt-2">{value}</div>
    </div>
  );
}
